"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cn } from "./utils";
import { useDialogFocus } from "./use-dialog-focus";

const MODAL_EASE = [0.22, 1, 0.36, 1] as const;
/**
 * Document scroll lock.
 *
 * `overflow: hidden` on the root is the usual reflex and it is wrong here on
 * both counts. It does not actually stop the document scrolling — iOS Safari
 * ignores it outright, and even Chrome still honours programmatic scrolls —
 * and it turns the root into a non-scrolling container, which un-sticks a
 * `position: sticky` header. Behind a translucent, blurred backdrop that reads
 * as the page lurching sideways the instant a modal opens.
 *
 * Pinning the body instead keeps the document exactly where it was: the offset
 * cancels the scroll position, so nothing moves, the sticky header stays put,
 * and there is no scrollable document left to scroll.
 *
 * Ref-counted, because modals stack. Only the outermost one may capture and
 * restore the scroll position — an inner modal would otherwise record the
 * already-locked position (0) and restore the page to the top on close.
 */
let scrollLockDepth = 0;
let scrollLockRestoreY = 0;
let scrollLockPreviousStyles: {
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
} | null = null;

/**
 * Marks the document while ANY modal is open, so page-level touch gestures
 * can stand down. The app's edge-swipe navigation listens on the window: a
 * horizontal drag inside a modal (rotating the 360 viewer, say) also opened
 * the sidebar underneath it, and the user found it waiting for them after
 * closing the modal. Ref-counted with the scroll lock, so nested modals
 * cannot clear it early.
 */
export const MODAL_OPEN_BODY_ATTRIBUTE = "data-modal-open";

function acquireScrollLock(): void {
  scrollLockDepth += 1;
  if (scrollLockDepth > 1) return;

  const body = document.body;
  body.setAttribute(MODAL_OPEN_BODY_ATTRIBUTE, "true");
  scrollLockRestoreY = window.scrollY;
  scrollLockPreviousStyles = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
  };

  // Position only, deliberately: adding `overflow: hidden` here would un-stick
  // the header exactly as the root version did, and it buys nothing — a fixed
  // body is out of flow, so there is no document scroll left to suppress.
  body.style.position = "fixed";
  body.style.top = `-${scrollLockRestoreY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
}

function releaseScrollLock(): void {
  scrollLockDepth = Math.max(0, scrollLockDepth - 1);
  if (scrollLockDepth > 0 || !scrollLockPreviousStyles) return;

  const body = document.body;
  body.removeAttribute(MODAL_OPEN_BODY_ATTRIBUTE);
  body.style.position = scrollLockPreviousStyles.position;
  body.style.top = scrollLockPreviousStyles.top;
  body.style.left = scrollLockPreviousStyles.left;
  body.style.right = scrollLockPreviousStyles.right;
  body.style.width = scrollLockPreviousStyles.width;
  scrollLockPreviousStyles = null;
  // The lock zeroed the document scroll; put the reader back where they were.
  window.scrollTo(0, scrollLockRestoreY);
}

/** Reuses the same ref-counted body lock for specialized dialog surfaces. */
export function useModalScrollLock(open: boolean) {
  React.useEffect(() => {
    if (!open) return;
    acquireScrollLock();
    return releaseScrollLock;
  }, [open]);
}

type ModalAccessibleName =
  | { ariaLabel: string; ariaLabelledBy?: string }
  | { ariaLabel?: string; ariaLabelledBy: string };

export type ModalProps = ModalAccessibleName & {
  open: boolean;
  children: React.ReactNode;
  /** Invoked on Escape and backdrop activation unless closing is disabled. */
  onClose?: () => void;
  /** Disables backdrop and Escape dismissal during a blocking operation. */
  closeDisabled?: boolean;
  lockScroll?: boolean;
  /** Stacking class, for example `z-80` or `z-[200]`. */
  zClassName?: string;
  /** Viewport bounds for a modal that must preserve surrounding app chrome. */
  viewportClassName?: string;
  /** Safe-area-aware viewport padding. */
  paddingClassName?: string;
  ariaDescribedBy?: string;
  /** Accessible name for the full-screen backdrop dismissal control. */
  backdropLabel: string;
};

/**
 * Accessible modal foundation shared by application dialogs.
 *
 * It portals out of transformed ancestors, moves focus inside, traps Tab,
 * restores focus on close, supports Escape/backdrop dismissal, locks document
 * scroll, and respects reduced-motion preferences. Dialog-specific layout and
 * actions stay with the consuming feature.
 */
export function Modal({
  open,
  children,
  onClose,
  closeDisabled = false,
  lockScroll = true,
  zClassName = "z-80",
  viewportClassName = "inset-0",
  paddingClassName = "p-4 pt-[max(1rem,var(--app-safe-top,0px))] pb-[max(1rem,var(--app-safe-bottom,0px))]",
  ariaLabelledBy,
  ariaDescribedBy,
  ariaLabel,
  backdropLabel,
}: ModalProps) {
  const shouldReduceMotion = useReducedMotion();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  useModalScrollLock(open && lockScroll);

  useDialogFocus({
    open: mounted && open,
    dialogRef,
    onClose,
    closeDisabled,
  });

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={dialogRef}
          key="carpybara-modal"
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: MODAL_EASE }}
          className={cn(
            "fixed flex items-center justify-center overflow-y-auto overscroll-none bg-(--modal-backdrop) backdrop-blur-sm",
            viewportClassName,
            paddingClassName,
            zClassName,
          )}
          data-app-modal-surface
          data-modal-surface
          role="dialog"
          tabIndex={-1}
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
        >
          {onClose ? (
            <button
              type="button"
              aria-label={backdropLabel}
              tabIndex={-1}
              className="absolute inset-0 size-full cursor-default"
              onClick={() => {
                if (!closeDisabled) onClose();
              }}
              disabled={closeDisabled}
            />
          ) : null}
          <motion.div
            initial={
              shouldReduceMotion ? false : { opacity: 0, scale: 0.94, y: 14 }
            }
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={
              shouldReduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.97, y: 6 }
            }
            transition={{ duration: 0.28, ease: MODAL_EASE }}
            style={{ willChange: "transform, opacity" }}
            className="relative flex h-full max-h-full w-full max-w-full items-center justify-center"
            onClick={(event) => {
              if (
                event.target === event.currentTarget &&
                onClose &&
                !closeDisabled
              ) {
                onClose();
              }
            }}
          >
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
